import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { handleAuthCallbackParams } from '@/lib/deep-links'
import { Loading } from '@/components/ui/loading'

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackRoute,
})

/**
 * Web magic-link landing. Native opens pronteer://auth/callback (handled by the
 * Capacitor deep-link listener); on the web build Supabase redirects the browser
 * here with ?code=… (or token_hash/type). We exchange it for a session in the
 * same browser, then route into the app — onAuthStateChange flips useAuth so the
 * _auth gate takes over.
 */
function AuthCallbackRoute() {
  const navigate = useNavigate()
  const ranRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Guard against double-invocation (StrictMode) — the code is single-use.
    if (ranRef.current) return
    ranRef.current = true

    void (async () => {
      const params = new URLSearchParams(window.location.search)
      const result = await handleAuthCallbackParams(params)
      if (result.error) {
        setError(result.error)
        toast.error(result.error)
        navigate({ to: '/signin', replace: true })
        return
      }
      // The web build is the desktop operations console — land on /operations
      // (there's no Home route on desktop). Native uses the deep-link handler,
      // not this route.
      navigate({ to: '/operations', replace: true })
    })()
  }, [navigate])

  return (
    <div className="flex h-full flex-1 items-center justify-center p-6">
      {error ? (
        <p className="text-sm text-content-primary-subtle">{error}</p>
      ) : (
        <Loading size={48} />
      )}
    </div>
  )
}
